import { notifications } from '@mantine/notifications';
import { type SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import {
  useArchiveSchemaMutation,
  useDraftSchemaMutation,
  usePublishSchemaMutation,
  useSchemaRegistryQuery,
  useSchemasQuery,
} from '../../../api/schemas';
import type { SchemaListItem } from '@morph-mapper/types';
import type { SchemaListFilters } from '../../../types';
import { getSchemaListColumns } from '../schema-list-columns';
import { safeAsync } from '@morph-mapper/utils';

type UseSchemaListOptions = {
  filters: SchemaListFilters;
};

export const useSchemaList = ({ filters }: UseSchemaListOptions) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'updatedAt', desc: true },
  ]);
  const [schemaToArchive, setSchemaToArchive] = useState<SchemaListItem | null>(
    null,
  );

  const { data: schemaRegistry } = useSchemaRegistryQuery();
  const { data = [], isLoading, isError, error } = useSchemasQuery(filters);
  const archiveSchemaMutation = useArchiveSchemaMutation();
  const publishSchemaMutation = usePublishSchemaMutation();
  const draftSchemaMutation = useDraftSchemaMutation();

  const handleArchiveRequest = (schema: SchemaListItem) => {
    setSchemaToArchive(schema);
  };

  const handleArchiveCancel = () => {
    setSchemaToArchive(null);
  };

  const handleArchiveConfirm = async () => {
    if (!schemaToArchive) {
      return;
    }

    setSchemaToArchive(null);

    const result = await safeAsync(() =>
      archiveSchemaMutation.mutateAsync(schemaToArchive.id),
    );

    if (result.isErr()) {
      notifications.show({
        color: 'red',
        message:
          result.error instanceof Error
            ? result.error.message
            : 'Failed to archive schema.',
      });
    } else {
      notifications.show({
        color: 'green',
        message: 'Schema archived.',
      });
    }
  };

  const handleStatusToggle = async (schema: SchemaListItem) => {
    const result = await safeAsync(() =>
      schema.status === 'draft'
        ? publishSchemaMutation.mutateAsync(schema.id)
        : draftSchemaMutation.mutateAsync(schema.id),
    );

    if (result.isErr()) {
      notifications.show({
        color: 'red',
        message:
          result.error instanceof Error
            ? result.error.message
            : 'Failed to update schema status.',
      });
      return;
    }

    notifications.show({
      color: 'green',
      message:
        result.value.status === 'active'
          ? 'Schema set to active.'
          : 'Schema set to inactive.',
    });
  };

  const columns = useMemo(
    () =>
      getSchemaListColumns({
        archivePendingSchemaId: archiveSchemaMutation.isPending
          ? archiveSchemaMutation.variables
          : undefined,
        onArchive: handleArchiveRequest,
        onToggleStatus: handleStatusToggle,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [archiveSchemaMutation.isPending, archiveSchemaMutation.variables],
  );

  return {
    columns,
    data,
    error,
    handleArchiveCancel,
    handleArchiveConfirm,
    isError,
    isLoading,
    schemaRegistry,
    schemaToArchive,
    setSorting,
    sorting,
  };
};
